import { Button, Checkbox, Input } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    OPERATION_TYPE,
    operationType,
    TREATMENT_TYPE,
    treatmentTypes,
    vesselType,
} from "../../../util/varibles/constants";
import { firstLetter } from "../../../util/varibles/global";
import { IIdName, IVessel } from "../../../util/varibles/interface";
import WrapperPopup from "../_components/WrapperPopup";
import Capabilities from "./Capabilities";
import styles from "./style.module.scss";

export const KEY_SPLIT = ",";

export const vesselOpType: { [id: string]: IIdName } = {
    [OPERATION_TYPE.HARVEST]: operationType[OPERATION_TYPE.HARVEST],
    [OPERATION_TYPE.TRANSPORT]: operationType[OPERATION_TYPE.TRANSPORT],
};

export const filterCapabilities = [
    [{ ...operationType[OPERATION_TYPE.HARVEST], id: `${OPERATION_TYPE.HARVEST}` }],
    treatmentTypes.map(item => {
        const id = [OPERATION_TYPE.TREATMENT, item.id].join(KEY_SPLIT);

        vesselOpType[id] = item;

        return { ...item, id };
    }),
    [{ ...operationType[OPERATION_TYPE.TRANSPORT], id: `${OPERATION_TYPE.TRANSPORT}` }],
];

export const generateFilterVesselKeys = ({
    operation_type = [],
    operation_sub_types = [],
    capabilities = [],
    type,
}: IVessel): string[] => {
    const keys = [
        ...operation_type.reduce((types: string[], sub) => {
            return vesselOpType[sub] ? [...types, `${sub}`] : types;
        }, []),

        ...operation_sub_types.reduce((types: string[], key: TREATMENT_TYPE) => {
            const sub = [OPERATION_TYPE.TREATMENT, key].join(KEY_SPLIT);

            return vesselOpType[sub] ? [...types, sub] : types;
        }, []),

        ...capabilities.map(item => [type, item.type].join(KEY_SPLIT)),
    ];

    return Array.from(new Set(keys));
};

type TVesselData = IVessel & {
    opType?: string[];
};

export const Vessel: React.FC<{
    data: TVesselData;
    width?: string;
    checked?: boolean;
    disabled?: boolean;
    onChange?(checked: boolean): void;
}> = ({ data, width = "380px", checked, disabled = false, onChange }) => {
    const { id, name, capabilities = [], capacity } = data;


    const opType = useMemo(() => {
        return data.opType || generateFilterVesselKeys(data);
    }, [data]);

    const checkboxProps: { checked?: boolean; disabled?: boolean; onChange?(e: any): void } =
        checked === undefined && !onChange
            ? {}
            : {
                checked,
                onChange: (e: any) => onChange?.(e.target.checked),
            };

    checkboxProps.disabled = disabled;

    return (
        <div
            id={["filter", id].join("-")}
            className={styles.vessel}
            style={{ width }}
            data-disabled={disabled}
        >
            <Checkbox className={styles.checkbox} value={id} {...checkboxProps}>
                <div className={styles.vesselName}>
                    <span className="mr-1">{name}</span>
                    <span className="text-comment italic font-[14px] font-[400]">
                        {capacity ? `- ${Math.round(capacity / 1000000)} t` : ""}
                    </span>
                </div>

                <div className={styles.opType}>
                    {[
                        ...opType.reduce((list: string[], item) => {
                            const { name = null } = vesselOpType[item] || {};
                            return name ? [...list, name] : list;
                        }, []),
                        ...capabilities.reduce((list: string[], item) => {
                            const { name = null } = item || {};
                            return name ? [...list, firstLetter(name)] : list;
                        }, []),
                    ].join(", ")}
                </div>
            </Checkbox>
        </div>
    );
};

// ------------------------------------------------------------------------------------------------------------------

interface IProps {
    selected: string[];
    vessels: IVessel[];
    showCapabilities?: boolean;
    locked?: string[];

    onClose(): void;

    onSave(planVessels: string[]): void;
}

interface IFilter {
    name: string;
    capabilities: string[];
}

const EMPTY_LOCKED: string[] = [];

const isSameSelected = (current: string[], selected: string[]) => {
    if (current.length !== selected.length) return false;

    const ids = new Set(current);

    return selected.every(id => ids.has(id));
};

const PopupFilterVessel: React.FC<IProps> = ({
    vessels,
    selected,
    showCapabilities = true,
    locked = EMPTY_LOCKED,
    onSave,
    onClose,
}) => {
    const [visible, setVisible] = useState(true);
    const [value, setValue] = useState<string[]>([]);
    const [view, setView] = useState<{ [index: string]: TVesselData[] }>({});
    const [filter, setFilter] = useState<IFilter>({ name: "", capabilities: [] });

    const list = useRef<{ [id: string]: TVesselData[] }>({});

    useEffect(() => {
        if (vessels.length <= 0) {
            list.current = {};
            setView({});
            setValue([]);
            return;
        }

        const newValue: string[] = [];
        const selectedIds = new Set(selected);
        const lockedIds = new Set(locked);

        list.current = vessels.reduce((rs: { [id: string]: TVesselData[] }, item) => {
            const { id, type } = item;

            if (selectedIds.has(id) || lockedIds.has(id)) {
                newValue.push(id);
            }

            rs[type] = [
                ...(rs[type] || []),
                {
                    ...item,
                    opType: generateFilterVesselKeys(item),
                },
            ];

            return rs;
        }, {});

        setView(list.current);
        setValue(newValue);

    }, [locked, vessels, selected]);

    const layout = useMemo(() => {
        const largestSection = Math.max(0, ...Object.values(view).map(items => items.length));
        const viewportHeight = typeof window === "undefined" ? 900 : window.innerHeight;
        const viewportWidth = typeof window === "undefined" ? 1200 : window.innerWidth;
        const availableHeight = Math.max(120, viewportHeight - 160);
        const rowsPerColumn = Math.max(1, Math.floor((availableHeight - 40) / 72));
        const columnCount = Math.max(
            1,
            Object.values(view).reduce((count, items) => {
                return count + Math.ceil(items.length / rowsPerColumn);
            }, 0)
        );
        return {
            optionsHeight: `min(calc(100vh - 160px), ${Math.max(120, largestSection * 72 + 40)}px)`,
            popupWidth: Math.min(viewportWidth - 32, Math.max(420, columnCount * 400)),
        };
    }, [view]);

    const handleFilter = useCallback(
        (search: Partial<IFilter>) => {
            const newFilter: IFilter = {
                ...filter,
                ...search,
            };

            const searchName = newFilter.name.trim().toLowerCase();
            const selectedCapabilities = newFilter.capabilities.filter(Boolean);

            const filterByName = (item: TVesselData) => {
                if (!searchName) return true;

                if (searchName[0] === "*") {
                    return item.name.toLowerCase().includes(searchName.substring(1));
                }

                return item.name.toLowerCase().indexOf(searchName) === 0;
            };

            const filterByCapability = (item: TVesselData) => {
                if (selectedCapabilities.length <= 0) return true;

                const capabilities = new Set(selectedCapabilities);

                return (item.opType || []).some(type => capabilities.has(type.toString()));
            };

            const newView = Object.keys(list.current).reduce((rs: { [id: string]: TVesselData[] }, key) => {
                rs[key] = list.current[key].filter(vessel => {
                    return filterByName(vessel) && filterByCapability(vessel);
                });

                return rs;
            }, {});

            setFilter(newFilter);
            setView(newView);
        },
        [filter]
    );

    const handleClose = useCallback(() => {
        setVisible(false);

        if (!isSameSelected(value, selected)) {
            onSave(value);
        }

        setTimeout(onClose, 300);
    }, [value, selected, onSave, onClose]);

    return (
        <WrapperPopup
            visible={visible}
            width={layout.popupWidth}
            className={styles.modal}
            title={
                <div className={styles.modalTitle}>
                    <span>
                        Vessel filter {value.length > 0 ? `(${value.length}/${vessels.length})` : ""}
                    </span>
                    {value.some(id => !locked.includes(id)) && <Button
                            tabIndex={-1}
                            className="bt-default h-32"
                            onClick={() => setValue([...locked])}
                        >
                            Clear all
                        </Button>}
                </div>
            }
            onClose={handleClose}
        >
            <div className={styles.filter} data-show-capabilities={showCapabilities}>
                <Input
                    placeholder="Search by name"
                    value={filter.name}
                    onChange={e => handleFilter({ name: e.target.value })}
                />

                {showCapabilities && (
                    <Capabilities
                        vessels={vessels}
                        onChange={capabilities => handleFilter({ capabilities })}
                    />
                )}

            </div>

            <Checkbox.Group
                className={styles.options}
                style={{ height: layout.optionsHeight }}
                value={value}
                onChange={ids => setValue(ids as string[])}
            >
                {Object.keys(view).map(key => {
                    const { name = "" } = vesselType[key] || {};
                    const count = view[key].length;

                    return (
                        <div className={styles.section} key={key}>
                            <div className={styles.sectionStart}>
                                <div className={styles.sectionName}>
                                    {name} ({count})
                                </div>
                                {view[key][0] && <Vessel
                                    key={view[key][0].id}
                                    data={view[key][0]}
                                    disabled={locked.includes(view[key][0].id)}
                                />}
                            </div>

                            {view[key].slice(1).map(vessel => (
                                <Vessel key={vessel.id} data={vessel} disabled={locked.includes(vessel.id)} />
                            ))}
                        </div>
                    );
                })}

                <div />
            </Checkbox.Group>
        </WrapperPopup>
    );
};

export default PopupFilterVessel;
